import { Info, MoreVertical, Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Menu, type MenuItem } from '../Menu';
import { OtherInfoModal, type OtherInfoData } from './OtherInfoModal';

export type { MenuItem };

/**
 * Per-row kebab — reference §14. Owns both the menu and the audit popover it opens, so a
 * caller never has to wire Other Info itself.
 *
 * Item order is fixed: **Other Info** → extraItems → **Edit** → **Delete** (destructive,
 * last), with dividers inserted automatically. Permission-gated items are hidden by
 * `<Menu>`, not disabled.
 */
export function RowActionMenu({
  id,
  onEdit,
  onDelete,
  editPermission,
  deletePermission,
  /** Disables Delete with a reason rather than hiding it — for state, not permission. */
  deleteDisabledReason = null,
  deleteLabel = 'Delete',
  extraItems = [],
  hideDefaults = false,
  otherInfo = null,
}: {
  id: string;
  onEdit?: () => void;
  onDelete?: () => void;
  editPermission?: string;
  deletePermission?: string;
  deleteDisabledReason?: string | null;
  deleteLabel?: string;
  extraItems?: MenuItem[];
  hideDefaults?: boolean;
  otherInfo?: OtherInfoData | null;
}) {
  const [infoOpen, setInfoOpen] = useState(false);

  const items: MenuItem[] = [];

  if (otherInfo) {
    items.push({
      key: 'other-info',
      label: 'Other Info',
      icon: <Info className="h-4 w-4" aria-hidden="true" />,
      onClick: () => setInfoOpen(true),
    });
  }

  extraItems.forEach((item, index) => {
    items.push({ ...item, divider: index === 0 && items.length > 0 });
  });

  if (!hideDefaults && onEdit) {
    items.push({
      key: 'edit',
      label: 'Edit',
      icon: <Pencil className="h-4 w-4" aria-hidden="true" />,
      onClick: onEdit,
      permission: editPermission,
      divider: items.length > 0,
    });
  }

  if (!hideDefaults && onDelete) {
    items.push({
      key: 'delete',
      label: deleteLabel,
      icon: <Trash2 className="h-4 w-4" aria-hidden="true" />,
      onClick: onDelete,
      permission: deletePermission,
      destructive: true,
      disabledReason: deleteDisabledReason,
      divider: !onEdit && items.length > 0,
    });
  }

  /*
    NO entries, no kebab. A trigger that opens an empty panel is the dead affordance the
    hide-rule forbids, and `<Menu>` already declines to take over the browser's own context
    menu in this state — this is the matching half for the trigger.

    It is reachable now that `otherInfo` may be null: a DERIVED row has no provenance to
    state, and a read-only surface over one has no verb either, which leaves nothing to open.
    Permission filtering happens inside `<Menu>`, so a menu emptied by a GATE still draws its
    kebab — hiding that would say the row has no actions rather than that this reader has none,
    and the gutter would then be a different width per account.
  */
  if (items.length === 0) {
    return null;
  }

  return (
    <>
      <Menu
        id={`-menu`}
        items={items}
        /*
          Right-clicking anywhere on the row opens this same menu, with the same entries
          and the same permission filtering. The kebab is a discoverable affordance; the
          context menu is the shortcut, and they cannot drift because they are one menu.
        */
        contextMenuFor="tr, li"
        trigger={({ open, toggle }) => (
          <button
            type="button"
            id={id}
            onClick={toggle}
            aria-haspopup="menu"
            aria-expanded={open}
            aria-label="Row actions"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
          >
            <MoreVertical className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      />

      <OtherInfoModal
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        data={otherInfo}
        id={`${id}-other-info`}
      />
    </>
  );
}
